import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	CheckIcon,
	ChevronLeftIcon,
	ChevronRightIcon,
	ExclamationTriangleIcon,
	MapPinIcon,
} from '@heroicons/react/24/outline';
import { Alert, AppLoader } from '../../components/ui';
import type { StaffBooking, StaffNextBooking, StaffSalon } from '../../types/staffBookings';
import {
	MINUTES_IN_DAY,
	MIN_TRAVEL_MINUTES,
	STATUS_COLORS,
	UNKNOWN_SALON_COLOR,
	addMonths,
	bookingDateKey,
	calendarStatus,
	daysBetween,
	daysInRange,
	endMinutes,
	formatAddress,
	formatClock,
	formatClockShort,
	formatDay,
	formatDurationShort,
	formatMoney,
	formatRangeLabel,
	groupByDay,
	laneLayout,
	monthGridRange,
	monthStart,
	monthViewRange,
	nowMinutes,
	rangeFor,
	salonColor,
	salonShortName,
	sameMonth,
	shiftAnchor,
	startMinutes,
	summarize,
	todayKey,
	travelGaps,
	type CalendarMode,
	type ColorMode,
	type DateRange,
	type EventColor,
} from '../../utils/staffCalendar';
import BookingPopover from './BookingPopover';

/** Calendar preferences kept by the page so they survive a switch to List and back. */
export interface CalendarPrefs {
	mode: CalendarMode;
	/** The date the visible period is built around (YYYY-MM-DD). */
	anchor: string;
	/** First day of the month the mini calendar shows. */
	miniMonth: string;
	colorBy: ColorMode;
	hiddenLocationIds: number[];
}

export const defaultCalendarPrefs = (anchor: string): CalendarPrefs => ({
	mode: 'week',
	anchor,
	miniMonth: monthStart(anchor),
	colorBy: 'salon',
	hiddenLocationIds: [],
});

/** The server caps a calendar request at this many days (StaffDashboardService). */
const MAX_FETCH_DAYS = 92;

/**
 * The days a calendar request must cover: the visible period plus the mini calendar's grid.
 * When the mini calendar has been paged far from the visible period the two are too far apart
 * for one request, so only the visible period is fetched and the mini shows dots for the days
 * it has.
 */
export const calendarFetchRange = (prefs: CalendarPrefs): DateRange => {
	const main = rangeFor(prefs.mode, prefs.anchor);
	const mini = monthGridRange(prefs.miniMonth);
	const from = main.from < mini.from ? main.from : mini.from;
	const to = main.to > mini.to ? main.to : mini.to;
	return daysBetween(from, to) > MAX_FETCH_DAYS ? main : { from, to };
};

const HOUR_PX = 46;
const MONTH_PREVIEW_LIMIT = 3;
const GRADIENT = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';

interface BookingsCalendarProps {
	/** Every booking in the fetched range, all statuses, search already applied by the server. */
	bookings: StaffBooking[];
	salons: StaffSalon[];
	nextBooking: StaffNextBooking | null;
	isLoading: boolean;
	error: string | null;
	onRetry: () => void;
	onDismissError: () => void;
	prefs: CalendarPrefs;
	onPrefsChange: (patch: Partial<CalendarPrefs>) => void;
	canReviewInvoice: boolean;
	onShowInList: (booking: StaffBooking) => void;
	onOpenInvoice: (booking: StaffBooking) => void;
}

interface Popover {
	booking: StaffBooking;
	rect: DOMRect;
}

const segButton = (active: boolean): string =>
	`px-3.5 py-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#667eea] ${
		active
			? 'text-white'
			: 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
	}`;

const navButton =
	'h-10 min-w-10 px-3 rounded-lg border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-bold text-gray-700 dark:text-gray-200 hover:border-[#667eea] hover:text-[#667eea] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#667eea] inline-flex items-center justify-center';

const railCard =
	'rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3.5';
const railTitle =
	'mb-2.5 text-[11px] font-extrabold uppercase tracking-wider text-gray-500 dark:text-gray-400';

const BookingsCalendar = ({
	bookings,
	salons,
	nextBooking,
	isLoading,
	error,
	onRetry,
	onDismissError,
	prefs,
	onPrefsChange,
	canReviewInvoice,
	onShowInList,
	onOpenInvoice,
}: BookingsCalendarProps) => {
	const { mode, anchor, miniMonth, colorBy } = prefs;
	const today = todayKey();
	const range = useMemo(() => rangeFor(mode, anchor), [mode, anchor]);
	const [popover, setPopover] = useState<Popover | null>(null);
	const closePopover = useCallback(() => setPopover(null), []);

	// Any navigation or re-colouring closes the popover; it would otherwise float over a
	// different period.
	useEffect(() => {
		setPopover(null);
	}, [mode, anchor, colorBy]);

	const hidden = useMemo(() => new Set(prefs.hiddenLocationIds), [prefs.hiddenLocationIds]);
	const salonIndex = useMemo(
		() => new Map(salons.map((s, i) => [s.locationId, i] as const)),
		[salons]
	);
	const salonById = useMemo(() => new Map(salons.map(s => [s.locationId, s] as const)), [salons]);

	const colorFor = useCallback(
		(b: StaffBooking): EventColor => {
			if (colorBy === 'status') return STATUS_COLORS[calendarStatus(b, today)];
			const idx = b.location ? salonIndex.get(b.location.id) : undefined;
			return idx == null ? UNKNOWN_SALON_COLOR : salonColor(idx);
		},
		[colorBy, salonIndex, today]
	);
	const shortNameFor = useCallback(
		(b: StaffBooking): string => {
			const salon = b.location ? salonById.get(b.location.id) : undefined;
			return salonShortName(salon ?? b.location ?? {});
		},
		[salonById]
	);

	const inPeriod = useMemo(
		() =>
			bookings.filter(b => bookingDateKey(b) >= range.from && bookingDateKey(b) <= range.to),
		[bookings, range]
	);
	const visible = useMemo(
		() => inPeriod.filter(b => !(b.location && hidden.has(b.location.id))),
		[inPeriod, hidden]
	);
	const visibleByDay = useMemo(() => groupByDay(visible), [visible]);
	const summary = useMemo(() => summarize(visible), [visible]);

	// Travel is checked over every booking in the period, hidden salons included, so switching a
	// salon off can never hide a tight transition.
	const travelIssues = useMemo(() => {
		const byDay = groupByDay(inPeriod);
		return [...byDay.entries()].flatMap(([day, items]) =>
			travelGaps(items)
				.filter(g => g.tight)
				.map(g => ({ day, ...g }))
		);
	}, [inPeriod]);

	const openPopover = (booking: StaffBooking, el: HTMLElement) =>
		setPopover({ booking, rect: el.getBoundingClientRect() });

	const goDay = (day: string) =>
		onPrefsChange({ mode: 'day', anchor: day, miniMonth: monthStart(day) });
	const navigate = (steps: number) => {
		const next = shiftAnchor(mode, anchor, steps);
		onPrefsChange({ anchor: next, miniMonth: monthStart(next) });
	};
	const goToday = () => onPrefsChange({ anchor: today, miniMonth: monthStart(today) });

	const toggleLocation = (id: number) =>
		onPrefsChange({
			hiddenLocationIds: hidden.has(id)
				? prefs.hiddenLocationIds.filter(x => x !== id)
				: [...prefs.hiddenLocationIds, id],
		});

	const periodLabel = mode === 'day' ? 'This day' : mode === 'week' ? 'This week' : 'This month';

	return (
		<div className="rounded-xl bg-white dark:bg-gray-800 p-4 sm:p-5 shadow-sm">
			{/* Toolbar */}
			<div className="mb-4 flex flex-wrap items-center gap-3">
				<div className="flex gap-1.5" role="group" aria-label="Calendar navigation">
					<button
						type="button"
						className={navButton}
						onClick={() => navigate(-1)}
						aria-label="Previous"
					>
						<ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
					</button>
					<button type="button" className={navButton} onClick={goToday}>
						Today
					</button>
					<button
						type="button"
						className={navButton}
						onClick={() => navigate(1)}
						aria-label="Next"
					>
						<ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
					</button>
				</div>
				<h2
					className="order-first w-full text-lg font-extrabold text-gray-900 dark:text-white sm:order-none sm:mr-auto sm:w-auto sm:text-xl"
					aria-live="polite"
				>
					{formatRangeLabel(mode, range)}
				</h2>
				<div
					className="flex flex-1 overflow-hidden rounded-lg border-2 border-gray-200 dark:border-gray-600 sm:flex-none"
					role="group"
					aria-label="Calendar mode"
				>
					{(['day', 'week', 'month'] as CalendarMode[]).map(m => (
						<button
							key={m}
							type="button"
							aria-pressed={mode === m}
							onClick={() => onPrefsChange({ mode: m })}
							className={`flex-1 border-l border-gray-200 dark:border-gray-600 first:border-l-0 ${segButton(mode === m)}`}
							style={mode === m ? { background: GRADIENT } : undefined}
						>
							{m === 'day' ? 'Day' : m === 'week' ? 'Week' : 'Month'}
						</button>
					))}
				</div>
				<div className="flex w-full items-center gap-2 text-xs font-bold text-gray-500 dark:text-gray-400 sm:w-auto">
					Color by
					<div
						className="flex flex-1 overflow-hidden rounded-lg border-2 border-gray-200 dark:border-gray-600 sm:flex-none"
						role="group"
						aria-label="Event colour"
					>
						{(['salon', 'status'] as ColorMode[]).map(c => (
							<button
								key={c}
								type="button"
								aria-pressed={colorBy === c}
								onClick={() => onPrefsChange({ colorBy: c })}
								className={`flex-1 border-l border-gray-200 dark:border-gray-600 first:border-l-0 ${segButton(colorBy === c)}`}
								style={colorBy === c ? { background: GRADIENT } : undefined}
							>
								{c === 'salon' ? 'Salon' : 'Status'}
							</button>
						))}
					</div>
				</div>
			</div>

			{error && (
				<Alert
					type="warning"
					icon={<ExclamationTriangleIcon className="w-8 h-8" />}
					title="Unable to Load the Calendar"
					message={error}
					actions={[{ label: 'Retry', onClick: onRetry, primary: true }]}
					onDismiss={onDismissError}
				/>
			)}

			<div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-[262px_minmax(0,1fr)]">
				{/* Rail */}
				<aside className="order-2 grid grid-cols-1 gap-3.5 sm:grid-cols-2 xl:order-none xl:sticky xl:top-4 xl:flex xl:flex-col">
					<MiniCalendar
						mode={mode}
						month={miniMonth}
						range={range}
						today={today}
						bookings={bookings}
						hidden={hidden}
						colorFor={colorFor}
						onMonthChange={m => onPrefsChange({ miniMonth: m })}
						onPick={day => onPrefsChange({ anchor: day })}
					/>

					<div className={railCard}>
						<h3 className={railTitle}>My salons</h3>
						{salons.length === 0 ? (
							<p className="text-sm text-gray-500 dark:text-gray-400">
								No salon engagements yet.
							</p>
						) : (
							salons.map((salon, i) => {
								const color = salonColor(i);
								const off = hidden.has(salon.locationId);
								const count = inPeriod.filter(
									b =>
										b.location?.id === salon.locationId &&
										b.status !== 'CANCELLED'
								).length;
								return (
									<button
										key={salon.locationId}
										type="button"
										aria-pressed={!off}
										title={[salon.salonName, formatAddress(salon)]
											.filter(Boolean)
											.join(' · ')}
										onClick={() => toggleLocation(salon.locationId)}
										className="flex w-full items-start gap-2.5 rounded-lg px-1.5 py-1.5 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#667eea]"
									>
										<span
											className="mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded border-2"
											style={{
												borderColor: color.border,
												background: off ? 'transparent' : color.border,
											}}
											aria-hidden="true"
										>
											{!off && (
												<CheckIcon
													className="h-3 w-3 text-white"
													strokeWidth={3.5}
												/>
											)}
										</span>
										<span className="min-w-0 flex-1 leading-tight">
											<span
												className={`block truncate font-bold ${off ? 'text-gray-400 dark:text-gray-500' : 'text-gray-800 dark:text-gray-100'}`}
											>
												{salonShortName(salon)}
											</span>
											<span className="block truncate text-xs font-semibold text-gray-500 dark:text-gray-400">
												{salon.salonName || '—'}
											</span>
											<span className="block truncate text-[11px] text-gray-400 dark:text-gray-500">
												{formatAddress(salon)}
											</span>
										</span>
										{count > 0 && (
											<span className="text-xs font-extrabold text-gray-500 dark:text-gray-400">
												{count}
											</span>
										)}
									</button>
								);
							})
						)}
					</div>

					<div className={railCard}>
						<h3 className={railTitle}>{periodLabel}</h3>
						<dl className="grid grid-cols-2 gap-x-2.5 gap-y-3">
							{[
								[String(summary.bookings), 'Bookings'],
								[formatDurationShort(summary.minutes), 'Chair time'],
								[formatMoney(summary.serviceValue), 'Service value'],
								[String(summary.salons), 'Salons'],
							].map(([value, label]) => (
								<div key={label}>
									<dd className="text-lg font-extrabold leading-tight text-gray-900 dark:text-white">
										{value}
									</dd>
									<dt className="text-xs font-semibold text-gray-500 dark:text-gray-400">
										{label}
									</dt>
								</div>
							))}
						</dl>
						<p className="mt-2 text-[11px] text-gray-400 dark:text-gray-500">
							Service value is the booked price before tips, not your earnings.
						</p>
						<div
							className={`mt-2.5 rounded-lg px-2.5 py-2 text-xs font-bold leading-snug ${
								travelIssues.length
									? 'bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-200'
									: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200'
							}`}
							role="status"
						>
							{travelIssues.length ? (
								<>
									&#9888; {travelIssues.length} tight move
									{travelIssues.length === 1 ? '' : 's'} between salons, under{' '}
									{MIN_TRAVEL_MINUTES} min to travel:{' '}
									{travelIssues
										.map(
											g =>
												`${formatDay(g.day, { month: 'short', day: 'numeric' })}: ${shortNameFor(g.from)} → ${shortNameFor(g.to)}`
										)
										.join('; ')}
									. Checked across all your salons for {periodLabel.toLowerCase()}
									.
								</>
							) : (
								<>
									&#10003; Travel check: every salon change in{' '}
									{periodLabel.toLowerCase()} has at least {MIN_TRAVEL_MINUTES}{' '}
									minutes, across all your salons.
								</>
							)}
						</div>
					</div>

					<div className={railCard}>
						<h3 className={railTitle}>Legend</h3>
						{colorBy === 'salon'
							? salons.map((salon, i) => (
									<LegendRow
										key={salon.locationId}
										swatch={salonColor(i).border}
										label={salonShortName(salon)}
									/>
								))
							: (Object.keys(STATUS_COLORS) as (keyof typeof STATUS_COLORS)[]).map(
									k => (
										<LegendRow
											key={k}
											swatch={STATUS_COLORS[k].border}
											label={STATUS_COLORS[k].label}
										/>
									)
								)}
						<LegendRow
							swatch="repeating-linear-gradient(135deg, #fee2e2 0 3px, #fff 3px 6px)"
							label="Cancelled (struck through)"
						/>
						<LegendRow
							swatch="repeating-linear-gradient(135deg, #fffbeb 0 3px, #fde68a 3px 6px)"
							label="Travel time between salons"
						/>
					</div>
				</aside>

				{/* Main */}
				<div className="relative order-1 min-w-0 xl:order-none">
					{isLoading && (
						<div className="absolute inset-0 z-20 flex items-center justify-center rounded-xl bg-white/70 dark:bg-gray-800/70">
							<AppLoader message="Loading calendar" variant="inline" />
						</div>
					)}
					{mode === 'month' ? (
						<MonthGrid
							monthKey={anchor}
							today={today}
							byDay={visibleByDay}
							colorFor={colorFor}
							shortNameFor={shortNameFor}
							onOpenDay={goDay}
							onOpenBooking={openPopover}
						/>
					) : (
						<TimeGrid
							days={daysInRange(range.from, range.to)}
							today={today}
							byDay={visibleByDay}
							colorFor={colorFor}
							shortNameFor={shortNameFor}
							onOpenDay={goDay}
							onOpenBooking={openPopover}
							nextBooking={nextBooking}
							onJumpTo={day =>
								onPrefsChange({ anchor: day, miniMonth: monthStart(day) })
							}
						/>
					)}
				</div>
			</div>

			{popover && (
				<BookingPopover
					booking={popover.booking}
					anchorRect={popover.rect}
					color={colorFor(popover.booking)}
					statusLabel={STATUS_COLORS[calendarStatus(popover.booking, today)].label}
					canReviewInvoice={canReviewInvoice}
					onClose={closePopover}
					onShowInList={onShowInList}
					onOpenDay={goDay}
					onOpenInvoice={onOpenInvoice}
				/>
			)}
		</div>
	);
};

const LegendRow = ({ swatch, label }: { swatch: string; label: string }) => (
	<div className="flex items-center gap-2 py-0.5 text-xs text-gray-700 dark:text-gray-200">
		<i
			className="h-[15px] w-[15px] shrink-0 rounded"
			style={{ background: swatch }}
			aria-hidden="true"
		/>
		{label}
	</div>
);

// ─── Mini calendar ────────────────────────────────────────────────────────

interface MiniCalendarProps {
	mode: CalendarMode;
	month: string;
	range: DateRange;
	today: string;
	bookings: StaffBooking[];
	hidden: Set<number>;
	colorFor: (b: StaffBooking) => EventColor;
	onMonthChange: (month: string) => void;
	onPick: (day: string) => void;
}

const MiniCalendar = ({
	mode,
	month,
	range,
	today,
	bookings,
	hidden,
	colorFor,
	onMonthChange,
	onPick,
}: MiniCalendarProps) => {
	const grid = monthGridRange(month);
	// Hovering a day previews the period a click would select (the day, its Mon–Sun week, or its
	// month), so the whole week lights up before it is picked, not only after.
	const [hovered, setHovered] = useState<string | null>(null);
	const hoverRange = hovered ? rangeFor(mode, hovered) : null;
	const byDay = useMemo(
		() =>
			groupByDay(
				bookings.filter(
					b =>
						bookingDateKey(b) >= grid.from &&
						bookingDateKey(b) <= grid.to &&
						!(b.location && hidden.has(b.location.id))
				)
			),
		[bookings, grid.from, grid.to, hidden]
	);
	const miniNav =
		'h-7 w-7 rounded-md text-lg leading-none text-gray-700 dark:text-gray-200 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#667eea]';
	return (
		<div className={railCard}>
			<div className="mb-1.5 flex items-center justify-between text-sm font-extrabold text-gray-900 dark:text-white">
				<button
					type="button"
					className={miniNav}
					onClick={() => onMonthChange(addMonths(month, -1))}
					aria-label="Previous month"
				>
					&#8249;
				</button>
				<span>{formatDay(month, { month: 'long', year: 'numeric' })}</span>
				<button
					type="button"
					className={miniNav}
					onClick={() => onMonthChange(addMonths(month, 1))}
					aria-label="Next month"
				>
					&#8250;
				</button>
			</div>
			<div
				className="grid grid-cols-7 gap-0.5 text-center"
				onMouseLeave={() => setHovered(null)}
			>
				{['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
					<div
						key={i}
						className="py-1 text-[11px] font-extrabold text-gray-500 dark:text-gray-400"
						aria-hidden="true"
					>
						{d}
					</div>
				))}
				{daysInRange(grid.from, grid.to).map(day => {
					const out = !sameMonth(day, month);
					const inRange = day >= range.from && day <= range.to;
					const inHover = !!hoverRange && day >= hoverRange.from && day <= hoverRange.to;
					const items = byDay.get(day) ?? [];
					return (
						<button
							key={day}
							type="button"
							onClick={() => onPick(day)}
							onMouseEnter={() => setHovered(day)}
							onFocus={() => setHovered(day)}
							onBlur={() => setHovered(null)}
							aria-label={`${formatDay(day, { weekday: 'long', month: 'long', day: 'numeric' })}${items.length ? `, ${items.length} booking${items.length === 1 ? '' : 's'}` : ''}`}
							aria-current={day === today ? 'date' : undefined}
							className={`relative flex h-[30px] cursor-pointer items-center justify-center rounded-md text-[13px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#667eea] ${
								inRange
									? 'bg-indigo-50 text-[#5a4fcf] dark:bg-indigo-900/40 dark:text-indigo-200'
									: inHover
										? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-100'
										: out
											? 'text-gray-300 dark:text-gray-600'
											: 'text-gray-800 dark:text-gray-100'
							} ${day === today ? 'ring-[1.5px] ring-inset ring-[#667eea]' : ''}`}
						>
							{Number(day.slice(8, 10))}
							{items.length > 0 && (
								<span
									className="absolute bottom-[3px] left-1/2 flex -translate-x-1/2 gap-0.5"
									aria-hidden="true"
								>
									{items.slice(0, 3).map(b => (
										<em
											key={b.id}
											className="block h-1 w-1 rounded-full"
											style={{ background: colorFor(b).border }}
										/>
									))}
								</span>
							)}
						</button>
					);
				})}
			</div>
		</div>
	);
};

// ─── Day / week timeline ──────────────────────────────────────────────────

interface TimeGridProps {
	days: string[];
	today: string;
	byDay: Map<string, StaffBooking[]>;
	colorFor: (b: StaffBooking) => EventColor;
	shortNameFor: (b: StaffBooking) => string;
	onOpenDay: (day: string) => void;
	onOpenBooking: (b: StaffBooking, el: HTMLElement) => void;
	nextBooking: StaffNextBooking | null;
	onJumpTo: (day: string) => void;
}

const TimeGrid = ({
	days,
	today,
	byDay,
	colorFor,
	shortNameFor,
	onOpenDay,
	onOpenBooking,
	nextBooking,
	onJumpTo,
}: TimeGridProps) => {
	const scrollRef = useRef<HTMLDivElement>(null);
	const single = days.length === 1;
	const bodyHeight = 24 * HOUR_PX;

	// Current-time line on today's column, on the salon clock, refreshed each minute.
	const [now, setNow] = useState(() => nowMinutes());
	useEffect(() => {
		const id = window.setInterval(() => setNow(nowMinutes()), 60_000);
		return () => window.clearInterval(id);
	}, []);

	const earliest = useMemo(() => {
		let first = MINUTES_IN_DAY;
		for (const day of days) {
			for (const b of byDay.get(day) ?? []) first = Math.min(first, startMinutes(b));
		}
		return first;
	}, [days, byDay]);
	const total = days.reduce((n, day) => n + (byDay.get(day)?.length ?? 0), 0);

	// Scroll to an hour before the earliest appointment; 8 AM when the period is empty.
	useEffect(() => {
		const el = scrollRef.current;
		if (!el) return;
		const first = earliest === MINUTES_IN_DAY ? 8 * 60 : earliest;
		el.scrollTop = Math.max(0, ((first - 60) / 60) * HOUR_PX);
	}, [days, earliest]);

	const lastDay = days[days.length - 1];
	const nextAfter =
		nextBooking && nextBooking.appointmentDateTime.slice(0, 10) > lastDay ? nextBooking : null;

	return (
		<div
			ref={scrollRef}
			className="relative max-h-[720px] overflow-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
			role="region"
			aria-label={single ? 'Day timeline' : 'Week timeline'}
		>
			<div
				className="grid min-w-full"
				style={{
					gridTemplateColumns: `58px repeat(${days.length}, minmax(${single ? 300 : 118}px, 1fr))`,
				}}
			>
				<div className="sticky left-0 top-0 z-[9] h-[72px] border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800" />
				{days.map(day => {
					const live = (byDay.get(day) ?? []).filter(b => b.status !== 'CANCELLED');
					const minutes = live.reduce((n, b) => n + (b.durationMinutes || 0), 0);
					const locs = [...new Set(live.map(shortNameFor))];
					const isToday = day === today;
					const inner = (
						<>
							<div className="flex items-baseline gap-1.5">
								<span className="text-[11px] font-extrabold tracking-wider text-gray-500 dark:text-gray-400">
									{formatDay(day, { weekday: 'short' }).toUpperCase()}
								</span>
								<span
									className={`text-xl font-extrabold leading-tight ${isToday ? 'rounded-md px-1.5 text-white' : 'text-gray-900 dark:text-white'}`}
									style={isToday ? { background: GRADIENT } : undefined}
								>
									{Number(day.slice(8, 10))}
								</span>
								{single && (
									<span className="text-[11px] font-extrabold tracking-wider text-gray-500 dark:text-gray-400">
										{formatDay(day, { month: 'long', year: 'numeric' })}
									</span>
								)}
							</div>
							<div className="mt-0.5 truncate text-xs font-bold text-gray-500 dark:text-gray-400">
								{live.length
									? `${live.length} booking${live.length === 1 ? '' : 's'} · ${formatDurationShort(minutes)} · ${locs.join(', ')}`
									: 'Free'}
							</div>
						</>
					);
					const headClass =
						'sticky top-0 z-[7] flex h-[72px] min-w-0 flex-col justify-center border-b border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-left';
					return single ? (
						<div key={day} className={headClass}>
							{inner}
						</div>
					) : (
						<button
							key={day}
							type="button"
							className={`${headClass} hover:bg-gray-50 dark:hover:bg-gray-700/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#667eea]`}
							title={`Open ${formatDay(day, { weekday: 'long', month: 'long', day: 'numeric' })}`}
							onClick={() => onOpenDay(day)}
						>
							{inner}
						</button>
					);
				})}

				{/* Hour gutter */}
				<div
					className="sticky left-0 z-[6] border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
					style={{ height: bodyHeight, position: 'sticky' }}
					aria-hidden="true"
				>
					<div className="relative h-full">
						{Array.from({ length: 23 }, (_, i) => i + 1).map(h => (
							<div
								key={h}
								className="absolute right-2 -translate-y-1/2 whitespace-nowrap text-[11px] font-bold text-gray-500 dark:text-gray-400"
								style={{ top: h * HOUR_PX }}
							>
								{formatClock(h * 60).replace(':00', '')}
							</div>
						))}
					</div>
				</div>

				{days.map(day => {
					const items = byDay.get(day) ?? [];
					const lanes = laneLayout(items);
					const gaps = travelGaps(items);
					const isToday = day === today;
					return (
						<div
							key={day}
							className={`relative border-l border-gray-200 dark:border-gray-700 ${isToday ? 'bg-indigo-50/40 dark:bg-indigo-900/10' : ''}`}
							style={{
								height: bodyHeight,
								backgroundImage:
									'linear-gradient(to bottom, rgba(148,163,184,.35) 1px, transparent 1px), linear-gradient(to bottom, rgba(148,163,184,.15) 1px, transparent 1px)',
								backgroundSize: `100% ${HOUR_PX}px, 100% ${HOUR_PX / 2}px`,
							}}
						>
							{items.map(b => {
								const s = startMinutes(b);
								const e = Math.min(MINUTES_IN_DAY, endMinutes(b));
								const realEnd = endMinutes(b);
								const height = Math.max(22, ((e - s) / 60) * HOUR_PX - 2);
								const place = lanes.get(b.id) ?? { lane: 0, lanes: 1 };
								const offset = place.lane * Math.min(30, 60 / place.lanes);
								const color = colorFor(b);
								const cancelled = b.status === 'CANCELLED';
								const short = height < 48;
								const timeText = `${formatClock(s)} – ${formatClock(realEnd)}${realEnd > MINUTES_IN_DAY ? ' (+1)' : ''}`;
								return (
									<button
										key={b.id}
										type="button"
										onClick={ev => {
											ev.stopPropagation();
											onOpenBooking(b, ev.currentTarget);
										}}
										title={`${b.customerName} — ${b.serviceName} · ${shortNameFor(b)}`}
										aria-label={`${b.customerName}, ${timeText}, ${b.serviceName}, ${shortNameFor(b)}${cancelled ? ', cancelled' : ''}`}
										className={`absolute overflow-hidden rounded-lg border-l-4 text-left shadow-sm transition-shadow hover:z-[5] hover:shadow-lg focus-visible:z-[5] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#667eea] ${
											short
												? 'flex items-center gap-1.5 px-2 py-0.5'
												: 'px-2 py-1.5'
										}`}
										style={{
											top: (s / 60) * HOUR_PX,
											height,
											left: `calc(${offset}% + 4px)`,
											width: `calc(${100 - offset}% - 8px)`,
											zIndex: 3 + place.lane,
											background: color.background,
											borderLeftColor: color.border,
											color: color.text,
											backgroundImage: cancelled
												? 'repeating-linear-gradient(135deg, transparent 0 6px, rgba(255,255,255,.6) 6px 12px)'
												: undefined,
										}}
									>
										<div className="truncate text-[11px] font-bold opacity-90">
											{timeText}
										</div>
										<div
											className={`truncate font-extrabold leading-tight ${short ? 'text-xs' : 'text-sm'} ${cancelled ? 'line-through' : ''}`}
										>
											{b.customerName}
										</div>
										{!short && (
											<>
												<div className="mt-0.5 truncate text-xs">
													{b.serviceName} &middot; {formatMoney(b.amount)}
												</div>
												<div className="mt-1 flex items-center gap-1 text-[11px] font-bold opacity-85">
													<MapPinIcon
														className="h-3 w-3 shrink-0"
														aria-hidden="true"
													/>
													<span className="truncate">
														{shortNameFor(b)}
													</span>
												</div>
												{height > 120 &&
													b.assignedTasks &&
													b.assignedTasks.length > 0 && (
														<div className="mt-1 flex flex-wrap gap-1">
															{b.assignedTasks.map(t => (
																<span
																	key={t}
																	className="rounded bg-white/75 px-1.5 text-[10px] font-extrabold text-gray-800"
																>
																	{t}
																</span>
															))}
														</div>
													)}
											</>
										)}
									</button>
								);
							})}

							{gaps.map(g => {
								const top = (endMinutes(g.from) / 60) * HOUR_PX;
								const len = g.tight
									? Math.max(g.gapMinutes, 15)
									: MIN_TRAVEL_MINUTES;
								return (
									<div
										key={`${g.from.id}-${g.to.id}`}
										className={`absolute left-2.5 right-2.5 z-[2] flex items-center justify-center gap-1 overflow-hidden rounded-md border-[1.5px] border-dashed px-1 text-center text-[11px] font-extrabold ${
											g.tight
												? 'border-red-500 text-red-900'
												: 'border-amber-500 text-amber-900'
										}`}
										style={{
											top,
											height: Math.max(18, (len / 60) * HOUR_PX - 2),
											background: g.tight
												? 'repeating-linear-gradient(135deg, #fef2f2 0 5px, #fee2e2 5px 10px)'
												: 'repeating-linear-gradient(135deg, #fffbeb 0 5px, #fef3c7 5px 10px)',
										}}
										title={
											g.tight
												? `Less than ${MIN_TRAVEL_MINUTES} minutes between salons. Ask the salon to adjust one booking.`
												: `Travel buffer to ${shortNameFor(g.to)}`
										}
									>
										{g.tight
											? `⚠ ${Math.max(0, g.gapMinutes)} min to ${shortNameFor(g.to)}`
											: `🚗 ${MIN_TRAVEL_MINUTES} min to ${shortNameFor(g.to)}`}
									</div>
								);
							})}

							{isToday && (
								<div
									className="pointer-events-none absolute left-0 right-0 z-[6] h-0.5 bg-red-500 before:absolute before:-left-[5px] before:-top-1 before:h-2.5 before:w-2.5 before:rounded-full before:bg-red-500"
									style={{ top: (now / 60) * HOUR_PX }}
									aria-hidden="true"
								/>
							)}
						</div>
					);
				})}
			</div>

			{total === 0 && (
				<div className="absolute left-1/2 top-[120px] z-[4] w-[min(380px,calc(100%-30px))] -translate-x-1/2 rounded-2xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-5 py-4 text-center shadow-xl">
					<b className="block text-base font-extrabold text-gray-900 dark:text-white">
						No bookings {single ? 'this day' : 'this week'}
					</b>
					<span className="text-sm text-gray-500 dark:text-gray-400">
						{nextAfter
							? `Next: ${nextAfter.customerName} · ${formatDay(nextAfter.appointmentDateTime.slice(0, 10), { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}`
							: 'Nothing else is scheduled.'}
					</span>
					{nextAfter && (
						<button
							type="button"
							onClick={() => onJumpTo(nextAfter.appointmentDateTime.slice(0, 10))}
							className="mt-3 rounded-lg px-4 py-2 text-sm font-extrabold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#667eea] focus-visible:ring-offset-2"
							style={{ background: GRADIENT }}
						>
							Jump to next booking
						</button>
					)}
				</div>
			)}
		</div>
	);
};

// ─── Month grid ───────────────────────────────────────────────────────────

interface MonthGridProps {
	monthKey: string;
	today: string;
	byDay: Map<string, StaffBooking[]>;
	colorFor: (b: StaffBooking) => EventColor;
	shortNameFor: (b: StaffBooking) => string;
	onOpenDay: (day: string) => void;
	onOpenBooking: (b: StaffBooking, el: HTMLElement) => void;
}

const MonthGrid = ({
	monthKey,
	today,
	byDay,
	colorFor,
	shortNameFor,
	onOpenDay,
	onOpenBooking,
}: MonthGridProps) => {
	const view = monthViewRange(monthKey);
	return (
		<div
			className="grid grid-cols-7 overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
			role="grid"
			aria-label={formatDay(monthKey, { month: 'long', year: 'numeric' })}
		>
			{['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'].map(d => (
				<div
					key={d}
					role="columnheader"
					className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-1 py-2 text-center text-[11px] font-extrabold tracking-wider text-gray-500 dark:text-gray-400 sm:px-3 sm:text-left"
				>
					{d}
				</div>
			))}
			{daysInRange(view.from, view.to).map(day => {
				const items = byDay.get(day) ?? [];
				const minutes = items
					.filter(b => b.status !== 'CANCELLED')
					.reduce((n, b) => n + (b.durationMinutes || 0), 0);
				const out = !sameMonth(day, monthKey);
				const isToday = day === today;
				const extra = items.length - MONTH_PREVIEW_LIMIT;
				return (
					<div
						key={day}
						role="gridcell"
						tabIndex={0}
						aria-label={`${formatDay(day, { weekday: 'long', month: 'long', day: 'numeric' })}, ${items.length} booking${items.length === 1 ? '' : 's'}. Open day`}
						onClick={() => onOpenDay(day)}
						onKeyDown={e => {
							if (e.target !== e.currentTarget) return;
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault();
								onOpenDay(day);
							}
						}}
						className={`flex min-h-[62px] min-w-0 cursor-pointer flex-col gap-1 border-b border-r border-gray-100 dark:border-gray-700 p-1.5 hover:shadow-[inset_0_0_0_2px_#667eea] focus-visible:outline-none focus-visible:shadow-[inset_0_0_0_2px_#667eea] sm:min-h-[124px] sm:p-2 [&:nth-child(7n)]:border-r-0 ${
							out ? 'bg-gray-50 dark:bg-gray-900/30' : ''
						}`}
					>
						<div className="flex items-center justify-between">
							<span
								className={`text-sm font-extrabold ${
									isToday
										? 'rounded-md px-1.5 text-white'
										: out
											? 'text-gray-300 dark:text-gray-600'
											: 'text-gray-900 dark:text-white'
								}`}
								style={isToday ? { background: GRADIENT } : undefined}
							>
								{Number(day.slice(8, 10))}
							</span>
							{minutes > 0 && (
								<span className="hidden rounded-full bg-indigo-50 dark:bg-indigo-900/40 px-1.5 py-0.5 text-[11px] font-extrabold text-[#5a4fcf] dark:text-indigo-200 sm:inline">
									{formatDurationShort(minutes)}
								</span>
							)}
						</div>
						{items.slice(0, MONTH_PREVIEW_LIMIT).map(b => {
							const color = colorFor(b);
							const cancelled = b.status === 'CANCELLED';
							return (
								<button
									key={b.id}
									type="button"
									onClick={e => {
										e.stopPropagation();
										onOpenBooking(b, e.currentTarget);
									}}
									title={`${b.customerName} — ${b.serviceName} · ${shortNameFor(b)}`}
									className={`hidden w-full truncate rounded border-l-[3px] px-1.5 py-0.5 text-left text-xs font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#667eea] sm:block ${cancelled ? 'line-through opacity-75' : ''}`}
									style={{
										background: color.background,
										borderLeftColor: color.border,
										color: color.text,
									}}
								>
									{formatClockShort(startMinutes(b))} {b.customerName}
								</button>
							);
						})}
						{extra > 0 && (
							<div className="hidden text-xs font-extrabold text-[#5a4fcf] dark:text-indigo-300 sm:block">
								+{extra} more
							</div>
						)}
						{items.length > 0 && (
							<div className="text-[11px] font-extrabold text-[#5a4fcf] dark:text-indigo-300 sm:hidden">
								{items.length} booking{items.length === 1 ? '' : 's'}
							</div>
						)}
					</div>
				);
			})}
		</div>
	);
};

export default BookingsCalendar;
